import { useState, useMemo, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { getWallet } from '../../components/actions/voucher';
import Loader from '../../components/utils/Loader';
import { getCustomer } from '../../components/actions/campaign';

export default function WalletPage() {
  const code = sessionStorage.getItem('code');

  // ────── States ──────
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [walletSummary, setWalletSummary] = useState({
    totalBalance: 0,
    totalCredit: 0,
    totalDebit: 0,
    activeWallets: 0,
    pendingTransactions: 0,
    todayTransactions: 0,
  });
  const [transactions, setTransactions] = useState([]);          // raw from wallet API
  const [enrichedTxns, setEnrichedTxns] = useState([]);         // with customer data
  const [customerMap, setCustomerMap] = useState(new Map());    // id → customer
  const [error, setError] = useState(null);

  // ────── Filters & Pagination ──────
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // ────── Fetch Wallet ──────
  const fetchWalletData = useCallback(async () => {
    if (!code) {
      setError('Authentication code missing. Please login again.');
      setLoadingWallet(false);
      return;
    }

    setLoadingWallet(true);
    setError(null);

    try {
      const response = await getWallet(code);
      const walletReport = response?.data?.walletReport || [];
      const firstReport = Array.isArray(walletReport) && walletReport.length > 0 ? walletReport[0] : {};

      setWalletSummary({
        totalBalance: firstReport.TotalMerchantPoint || 0,
        totalCredit: firstReport.TotalCredits || 0,
        totalDebit: firstReport.TotalDebits || 0,
        activeWallets: 0,
        pendingTransactions: 0,
        todayTransactions: firstReport.TodaysTransactions || 0,
      });

      const rawTxns = Array.isArray(response?.data?.walletDetails)
        ? response.data.walletDetails
        : [];

      setTransactions(rawTxns);
    } catch (err) {
      console.error('Wallet API Error:', err);
      setError('Failed to fetch wallet data.');
      setWalletSummary({
        totalBalance: 0,
        totalCredit: 0,
        totalDebit: 0,
        activeWallets: 0,
        pendingTransactions: 0,
        todayTransactions: 0,
      });
      setTransactions([]);
    } finally {
      setLoadingWallet(false);
    }
  }, [code]);

  // ────── Fetch Customers (once) ──────
  const fetchCustomers = useCallback(async () => {
    if (!code) return;
    setLoadingCustomers(true);
    try {
      const resp = await getCustomer(code);   // <-- your existing API
      const list = resp?.data || [];
      const map = new Map();
      list.forEach(c => map.set(c.customer_id, c));
      setCustomerMap(map);
    } catch (err) {
      console.error('getCustomer API Error:', err);
      setError(prev => prev ? `${prev} | Failed to load customers.` : 'Failed to load customers.');
    } finally {
      setLoadingCustomers(false);
    }
  }, [code]);

  // ────── Enrich transactions with customer data ──────
  useEffect(() => {
    if (!transactions.length || !customerMap.size) {
      setEnrichedTxns([]);
      return;
    }

    const enriched = transactions.map(tx => {
      const cust = customerMap.get(tx.customerid) || {};
      return {
        ...tx,
        customerName: cust.name || '—',
        customerMobile: cust.mobile || '—',
        customerEmail: cust.email || null,
        customerCity: cust.city || null,
        customerCountry: cust.country || null,
        // keep original id for fallback
        customerId: tx.customerid,
      };
    });
    setEnrichedTxns(enriched);
  }, [transactions, customerMap]);

  // ────── Initial loads ──────
  useEffect(() => {
    fetchWalletData();
    fetchCustomers();
  }, [fetchWalletData, fetchCustomers]);

  // ────── Filtering & Pagination ──────
  const filteredTransactions = useMemo(() => {
    if (!Array.isArray(enrichedTxns)) return [];

    return enrichedTxns.filter(tx => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        (tx.customerName?.toLowerCase() || '').includes(term) ||
        (tx.customerid?.toLowerCase() || '').includes(term) ||
        (tx.TranType?.toLowerCase() || '').includes(term) ||
        (tx.tranid?.toLowerCase() || '').includes(term);

      const matchesStatus = statusFilter === 'All Status' || tx.status === statusFilter;
      const matchesType = typeFilter === 'All Types' || tx.TranType === typeFilter;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [enrichedTxns, searchTerm, statusFilter, typeFilter]);

  const totalPages = Math.ceil(filteredTransactions.length / entriesPerPage);
  const startIdx = (currentPage - 1) * entriesPerPage;
  const paginated = filteredTransactions.slice(startIdx, startIdx + entriesPerPage);

  // ────── UI Helpers ──────
  const viewTransaction = tx => {
    setSelectedTransaction(tx);
    setShowViewModal(true);
  };

  const getTypeColor = type => {
    switch (type) {
      case 'Credit': return 'text-green-600 bg-green-50';
      case 'Debit': return 'text-red-600 bg-red-50';
      case 'Transfer': return 'text-blue-600 bg-blue-50';
      case 'Refund': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };
  const getTypeIcon = type => {
    switch (type) {
      case 'Credit': return 'ri-arrow-down-circle-line';
      case 'Debit': return 'ri-arrow-up-circle-line';
      case 'Transfer': return 'ri-exchange-line';
      case 'Refund': return 'ri-refund-line';
      default: return 'ri-wallet-line';
    }
  };
  const getStatusColor = status => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // ────── Render ──────
  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">

        {/* Global Loader */}
        {(loadingWallet || loadingCustomers) && (
          <div className="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
            <Loader />
          </div>
        )}

        {/* Error Banner */}
        {error && !loadingWallet && !loadingCustomers && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-xl text-sm flex items-center justify-between">
            <span><i className="ri-error-warning-line mr-2"></i>{error}</span>
            <button onClick={() => { fetchWalletData(); fetchCustomers(); }} className="underline font-medium hover:no-underline">
              Retry
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Wallet Management</h1>
            <p className="text-gray-600 mt-1">Monitor and manage customer wallet transactions</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Total Wallet Points</p>
                <p className="text-3xl font-bold">{walletSummary.totalBalance.toLocaleString('en-IN')}</p>
                <p className="text-green-100 text-xs mt-1">Merchant Points</p>
              </div>
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                <i className="ri-wallet-line text-2xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <i className="ri-arrow-down-circle-line text-blue-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Used Points</p>
                <p className="text-2xl font-bold text-gray-900">{walletSummary.totalCredit.toLocaleString('en-IN')}</p>
                {/* <p className="text-green-600 text-xs font-semibold">Inflow</p> */}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <i className="ri-arrow-up-circle-line text-red-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Available Points </p>
                <p className="text-2xl font-bold text-gray-900">{walletSummary.totalBalance - walletSummary.totalCredit}</p>
                {/* <p className="text-red-600 text-xs font-semibold">Outflow</p> */}
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <i className="ri-user-line text-purple-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Active Wallets</p>
                <p className="text-2xl font-bold text-gray-900">{walletSummary.activeWallets}</p>
                <p className="text-gray-400 text-xs">Data not available</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <i className="ri-time-line text-yellow-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Pending Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{walletSummary.pendingTransactions}</p>
                <p className="text-gray-400 text-xs">Data not available</p>
              </div>
            </div>
          </div> */}
           <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <i className="ri-arrow-down-circle-line text-blue-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Customer Earned Points</p>
                <p className="text-2xl font-bold text-gray-900">{walletSummary.totalCredit.toLocaleString('en-IN')}</p>
                <p className="text-green-600 text-xs font-semibold">Inflow</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <i className="ri-arrow-up-circle-line text-red-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Customer Redeemed Points</p>
                <p className="text-2xl font-bold text-gray-900">{walletSummary.totalDebit.toLocaleString('en-IN')}</p>
                <p className="text-red-600 text-xs font-semibold">Outflow</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <i className="ri-calendar-line text-orange-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Today's Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{walletSummary.todayTransactions}</p>
                <p className="text-green-600 text-xs font-semibold">Updated today</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="relative w-full lg:w-80">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by name, ID, reference..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-200 text-sm bg-gray-50"
              />
            </div>

            <div className="flex gap-3 items-center w-full lg:w-auto">
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="appearance-none bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                >
                  <option value="All Status">All Status</option>
                  <option value="Completed">Completed</option>
                  <option value="Pending">Pending</option>
                  <option value="Failed">Failed</option>
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
              </div>

              <div className="relative">
                <select
                  value={typeFilter}
                  onChange={e => setTypeFilter(e.target.value)}
                  className="appearance-none bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                >
                  <option value="All Types">All Types</option>
                  <option value="Credit">Credit</option>
                  <option value="Debit">Debit</option>
                  <option value="Transfer">Transfer</option>
                  <option value="Refund">Refund</option>
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Entries Control */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show</span>
                <select
                  value={entriesPerPage}
                  onChange={e => setEntriesPerPage(Number(e.target.value))}
                  className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer pr-8"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              Showing {filteredTransactions.length} results
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th> */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th> */}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      {enrichedTxns.length === 0 && !loadingWallet && !loadingCustomers
                        ? 'No transactions available.'
                        : 'Loading transactions...'}
                    </td>
                  </tr>
                ) : (
                  paginated.map(tx => (
                    <tr key={tx.id || tx.tranid || Math.random()} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getTypeColor(tx.TranType)}`}>
                            <i className={`${getTypeIcon(tx.TranType)} text-lg`}></i>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{tx.tranid || '—'}</p>
                            <p className="text-xs text-gray-500 font-mono">{tx.reference || '—'}</p>
                          </div>
                        </div>
                      </td>

                      {/* ---- CUSTOMER COLUMN (NAME + ID) ---- */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{tx.customerName}</p>
                          <p className="text-xs text-gray-500 font-mono">{tx.customerId || '—'}</p>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTypeColor(tx.TranType)}`}>
                          {tx.TranType || '—'}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-semibold ${['Credit', 'Refund'].includes(tx.TranType) ? 'text-green-600' : 'text-red-600'}`}>
                          {['Credit', 'Refund'].includes(tx.TranType) ? '+' : '-'}
                          {(tx.earningvalue || 0).toLocaleString('en-IN')}
                        </span>
                      </td>
{/* 
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {(tx.balance || 0).toLocaleString('en-IN')}
                      </td> */}

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {tx.createdAt ? new Date(tx.createdAt).toLocaleDateString('en-IN') : '—'}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(tx.status)}`}>
                          {"Completed"}
                        </span>
                      </td>

                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button onClick={() => viewTransaction(tx)} className="text-blue-600 hover:text-blue-800">
                          <i className="ri-eye-line text-lg"></i>
                        </button>
                      </td> */}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {filteredTransactions.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mt-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {startIdx + 1} to {Math.min(startIdx + entriesPerPage, filteredTransactions.length)} of {filteredTransactions.length} entries
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1 + Math.max(0, currentPage - 3);
                  if (page > totalPages) return null;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 border rounded text-sm ${currentPage === page ? 'bg-blue-500 text-white border-blue-500' : 'border-gray-300 hover:bg-gray-50'}`}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ────── VIEW MODAL ────── */}
        {showViewModal && selectedTransaction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Wallet Transaction Details</h2>
                <button onClick={() => setShowViewModal(false)} className="text-gray-400 hover:text-gray-600">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="space-y-6">

                {/* Header with type icon */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${getTypeColor(selectedTransaction.TranType)}`}>
                      <i className={`${getTypeIcon(selectedTransaction.TranType)} text-2xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{selectedTransaction.TranType || 'Unknown'} Transaction</h3>
                      <p className="text-gray-600">{selectedTransaction.reference || 'No reference'}</p>
                    </div>
                  </div>
                </div>

                {/* Two-column grids */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  {/* Transaction Info */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Transaction Information</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between"><span className="text-gray-600">Transaction ID</span><span className="font-medium font-mono">{selectedTransaction.tranid || '—'}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Reference</span><span className="font-medium font-mono">{selectedTransaction.reference || '—'}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Date</span><span className="font-medium">{selectedTransaction.createdAt ? new Date(selectedTransaction.createdAt).toLocaleDateString('en-IN') : '—'}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Status</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedTransaction.status)}`}>{selectedTransaction.status || '—'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Customer Info (enriched) */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Customer Information</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between"><span className="text-gray-600">Name</span><span className="font-medium">{selectedTransaction.customerName}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Customer ID</span><span className="font-medium font-mono text-xs">{selectedTransaction.customerId || '—'}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Mobile</span><span className="font-medium">{selectedTransaction.customerMobile}</span></div>
                      {selectedTransaction.customerEmail && (
                        <div className="flex justify-between"><span className="text-gray-600">Email</span><span className="font-medium">{selectedTransaction.customerEmail}</span></div>
                      )}
                      {selectedTransaction.customerCity && (
                        <div className="flex justify-between"><span className="text-gray-600">City</span><span className="font-medium">{selectedTransaction.customerCity}</span></div>
                      )}
                      {selectedTransaction.customerCountry && (
                        <div className="flex justify-between"><span className="text-gray-600">Country</span><span className="font-medium">{selectedTransaction.customerCountry}</span></div>
                      )}
                      <div className="flex justify-between"><span className="text-gray-600">Current Points</span><span estrés className="font-semibold text-green-600">{(selectedTransaction.balance || 0).toLocaleString('en-IN')}</span></div>
                    </div>
                  </div>

                </div>

                {/* Amount Details */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Amount Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-gray-600 text-sm">Amount</p>
                      <p className={`text-2xl font-bold ${['Credit', 'Refund'].includes(selectedTransaction.TranType) ? 'text-green-600' : 'text-red-600'}`}>
                        {['Credit', 'Refund'].includes(selectedTransaction.TranType) ? '+' : '-'}{(selectedTransaction.earningvalue || 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Points</p>
                      <p className="text-2xl font-bold text-blue-600">{(selectedTransaction.balance || 0).toLocaleString('en-IN')}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Type</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getTypeColor(selectedTransaction.TranType)}`}>
                        {selectedTransaction.TranType || '—'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-600 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}